import {
  Box,
  HStack,
  Separator,
  Span,
  Stack,
  Text,
  defineStyle,
  Wrap,
} from '@chakra-ui/react';
import { Alert } from '@ui';
import React from 'react';

export interface SelectLabelProps {
  label: string;
  value: string | number | null | undefined;
  fallback?: string;
  disabled?: boolean;
  minWidth?: string;
  isRequired?: boolean;
  [key: string]: any;
}

export const SelectLabel = ({
  label,
  value,
  fallback,
  disabled = false,
  minWidth = '100px',
  isRequired = true,
  ...rest
}: SelectLabelProps) => {
  return (
    <HStack
      key={`label-${label}`}
      opacity={disabled ? 0.5 : 1}
      whiteSpace="nowrap"
      minWidth={minWidth}
      {...rest}
    >
      <Stack direction="row" align="center" gap={1}>
        <Text
          textStyle="fieldLabel.description"
          color="fg.subtle"
          minWidth={minWidth}
        >
          {label}
          {isRequired && (
            <Span as="span" color="red.500" ml="1" fontWeight="extrabold">
              *
            </Span>
          )}
          <Span mx="1">:</Span>
        </Text>
        {!disabled && value ? (
          <Text textStyle="fieldLabel.descriptionBold" color="tertiary.solid">
            {value}
          </Text>
        ) : (
          <Alert status="warning">{fallback ?? `Please select ${label}`}</Alert>
        )}
      </Stack>
    </HStack>
  );
};

interface SelectLabelsProps {
  items: SelectLabelProps[];
  orientation?: 'horizontal' | 'vertical';
  alignItems?: 'center' | 'flex-start';
  justifyContent?: 'center' | 'flex-start';
  [key: string]: any;
}

export const SelectLabels = ({
  items,
  orientation,
  alignItems,
  justifyContent,
  ...rest
}: SelectLabelsProps) => {
  const isSeperatorVertical = orientation === 'horizontal';
  return (
    <Wrap
      gap={orientation ? 1 : { base: 2, md: 10, lg: 20 }}
      columnGap={orientation ? undefined : { base: 2, md: 10, lg: 20 }}
      rowGap={orientation ? undefined : { base: 1, md: 2, lg: 3 }}
      flexDirection={
        orientation
          ? isSeperatorVertical
            ? 'row'
            : 'column'
          : { base: 'column', md: 'row' }
      }
      justifyContent={
        justifyContent
          ? justifyContent
          : orientation
            ? 'center'
            : { base: 'center', md: 'center' }
      }
      alignItems={
        alignItems
          ? alignItems
          : orientation
            ? 'center'
            : { base: 'flex-start', md: 'center' }
      }
    >
      {items.map((item, index) => (
        <React.Fragment key={`${item.label}-${index}`}>
          <SelectLabel {...item} {...rest} />
          {index !== items.length - 1 && (
            <Separator
              borderColor="border.subtle"
              orientation={
                orientation
                  ? isSeperatorVertical
                    ? 'vertical'
                    : 'horizontal'
                  : { base: 'horizontal', md: 'vertical' }
              }
              height={
                orientation
                  ? isSeperatorVertical
                    ? 5
                    : undefined
                  : { base: undefined, md: 5 }
              }
            />
          )}
        </React.Fragment>
      ))}
    </Wrap>
  );
};

const floatingLabel = ({ isBorder = false, colorPalette }: LabelStylesProps) =>
  defineStyle({
    pos: 'absolute',
    top: isBorder ? '-3' : '-2.5',
    color: colorPalette ? colorPalette + '.fg' : 'fg.muted',
    backgroundColor: 'transparent',
    zIndex: 1,
    insetStart: '2',
    fontWeight: 'normal',
    pointerEvents: 'none',
    transition: 'position',
    whiteSpace: 'nowrap',
    textStyle: 'floatingLabel',
  });

const placeholder = ({ colorPalette }: LabelStylesProps) =>
  defineStyle({
    color: 'fg.subtle',
    textStyle: 'caption.muted',
    fontSize: 'xs',
    minWidth: 'fit-content',
  });

const floatingLabelBorder = ({
  colorPalette,
  isBorder = true,
}: LabelStylesProps) =>
  defineStyle(
    isBorder
      ? {
          border: '1px solid',
          borderColor: colorPalette ? colorPalette + '.border' : 'border',
          borderRadius: 'sm',
          p: 2,
          m: 0,
          _focusWithin: {
            borderColor: colorPalette
              ? colorPalette + '.focusRing'
              : 'primary.focusRing',
          },
          height: '100%',
          display: 'flex',
          mx: 2,
        }
      : {},
  );

const childLabel = ({ colorPalette }: LabelStylesProps) =>
  defineStyle({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    textStyle: 'fieldLabel.items',
    textAlign: 'flex-start',
    color: colorPalette ? colorPalette + '.fg' : 'fg',
  });

const childLabelBorder = ({ colorPalette }: LabelStylesProps) =>
  defineStyle({
    border: '1px solid',
    borderColor: colorPalette ? colorPalette + '.border' : 'border',
    borderRadius: 'sm',
    p: 2.5,
    m: 0,
    _focusWithin: {
      borderColor: colorPalette
        ? colorPalette + '.focusRing'
        : 'primary.focusRing',
    },
    height: '100%',
    minHeight: 'auto',
    display: 'flex',
    justifyContent: 'space-between',
  });

const childLabelNoBorder = ({ colorPalette }: LabelStylesProps) =>
  defineStyle({
    border: 'none',
    bg: 'transparent',
    focusRing: 'none',
    p: 0,
    m: 0,
    minHeight: 'auto',
    display: 'flex',
    justifyContent: 'space-between',
  });

interface LabelStylesProps {
  colorPalette?: string;
  isBorder?: boolean;
}

export const labelStyles = Object.assign(
  (opts: LabelStylesProps) => childLabel(opts),
  {
    _default: childLabel,
    border: (opts: LabelStylesProps) => childLabelBorder(opts),
    noBorder: (opts: LabelStylesProps) => childLabelNoBorder(opts),
    floating: Object.assign((opts: LabelStylesProps) => floatingLabel(opts), {
      border: (opts: LabelStylesProps) => floatingLabelBorder(opts),
    }),
    placeholder: (opts: LabelStylesProps) => placeholder(opts),
  },
);
